// src/components/landing/gradient-minimalist/hollo-landing-gradient-minimalist.ts
import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { UiMessage } from "../../../types/chat";
import { AdditionalTypes } from "../../../types/chat";
import "../../audio-player/hollo-audio-player";
import "../../inline-recorder/hollo-inline-recorder";
import { landingStyles as holloLandingStylesGradientMinimalist } from "../../../styles/gradient-minimalist/landing-styles";
import {
  micIconSvg,
  phoneIconSvg,
  figmaCopySvg,
  checkIconSvg,
  figmaVolumeMaxSvg,
  pauseIconSvg,
  playIconSvg,
  deleteIconSvg,
  recordingDotSvg,
  sendUpIconSvg,
  stopIconSvg,
  sparklesIcon,
  sendMinimalSvg,
  videoSvg,
} from "../../icons";
import "../../streaming-text";
import { LandingMixin } from "../landing-mixin";
import { getUrlSuggestions } from "../../../apis/ai-api";

@customElement("hollo-landing-gradient-minimalist")
export class HolloLandingGradientMinimalist extends LandingMixin(LitElement) {
  @property() fileId = "";
  @property() alt = "";
  @property() fullName = "";
  @property({ type: Array }) messages: UiMessage[] = [];
  @property({ type: Boolean }) typing = false;
  @property({ type: Boolean }) live = false;
  @property() profileImageId = "";
  @property() currentVideoFor?: string;
  @state() private suggestions: string[] = [];
  @state() private suggestionsLoading = false;
  @state() private streamedMessageIds = new Set<string>();

  private onStreamComplete(id: string) {
    this.streamedMessageIds.add(id);
    this.requestUpdate();
  }

  private getNonGreetingMessages(): UiMessage[] {
    if (!this.messages || !Array.isArray(this.messages)) {
      return [];
    }
    return this.messages.filter((m) => {
      const msg = m as UiMessage & { additional_type?: string };
      return msg.additional_type !== AdditionalTypes.AI_GREETING;
    });
  }

  private async fetchUrlSuggestions(): Promise<void> {
    // Only fetch if we don't have suggestions yet and we're not already loading
    if (this.suggestions.length > 0 || this.suggestionsLoading) {
      return;
    }

    // Get current page URL
    const currentUrl =
      typeof window !== "undefined" ? window.location.href : "";
    if (!currentUrl) {
      return;
    }

    this.suggestionsLoading = true;
    try {
      const result = await getUrlSuggestions(currentUrl);
      if (result && result.questions.length > 0) {
        this.suggestions = result.questions;
      }
    } catch (error) {
      // Silently fail - fallback to default suggestions
      console.warn("Failed to fetch URL suggestions:", error);
    } finally {
      this.suggestionsLoading = false;
    }
  }

  private getSuggestionsToDisplay(): string[] {
    return this.suggestions;
  }

  private renderMessage(m: UiMessage) {
    return html`
      <div
        class="hollo-row ${m.is_human
          ? "hollo-right"
          : "hollo-left"} ${m.audioUrl && !m.is_human ? "hollo-isAiVoice" : ""}"
      >
        <div class="hollo-bubble-box ${m.audioUrl ? "hollo-audio-bubble" : ""}">
          <div class="hollo-bubble-box-inner">
            <div style="position: relative">
              <div class="hollo-bubble ">
                ${m.audioUrl
                  ? html`
                      <hollo-audio-player
                        .src=${m.audioUrl}
                        .autoplay=${!!m.autoplay}
                        .isAiVoice=${!m.is_human}
                        .forMessageId=${m.for_message_id}
                      ></hollo-audio-player>
                      ${m.content && !m.is_human
                        ? html`<div class="hollo-caption hollo-transcript">
                            ${!this.streamedMessageIds.has(m.id || "") &&
                            (m as any).additional_type !==
                              AdditionalTypes.AI_GREETING
                              ? html`<hollo-streaming-text
                                  .text=${m.content}
                                  @complete=${() =>
                                    this.onStreamComplete(m.id || "")}
                                ></hollo-streaming-text>`
                              : m.content}
                          </div>`
                        : null}
                    `
                  : html`${!m.is_human &&
                    m.content &&
                    !this.streamedMessageIds.has(m.id || "") &&
                    (m as any).additional_type !== AdditionalTypes.AI_GREETING
                      ? html`<hollo-streaming-text
                          .text=${m.content}
                          @complete=${() => this.onStreamComplete(m.id || "")}
                        ></hollo-streaming-text>`
                      : m.content || (m._optimistic ? "…" : "")}`}
                ${!m.is_human
                  ? html`<div class="hollo-message-footer">
                      <div class="hollo-message-icons">
                        ${m.content
                          ? html`<button
                              class="hollo-message-icon-btn ${this
                                .copiedMessageId === (m.id || "")
                                ? "hollo-icon-copied"
                                : ""}"
                              @click=${() =>
                                this.copyMessageText(
                                  m.content || "",
                                  m.id || ""
                                )}
                              title="Copy message"
                              aria-label="Copy message"
                            >
                              <span class="hollo-icon-wrapper">
                                ${this.copiedMessageId === (m.id || "")
                                  ? checkIconSvg(18)
                                  : figmaCopySvg(18)}
                              </span>
                            </button>`
                          : null}
                        ${!m.audioUrl
                          ? html`<button
                              class="hollo-message-icon-btn ${this.getAudioState(
                                m.id || ""
                              ) === "playing"
                                ? "hollo-audio-playing"
                                : ""}"
                              @click=${() => {
                                if (this.isAudioPlaying(m.id || "")) {
                                  window.dispatchEvent(
                                    new CustomEvent("hollo-toggle-audio", {
                                      detail: { forMessageId: m.id },
                                    })
                                  );
                                } else {
                                  this.requestAudio(m.id || "");
                                }
                              }}
                              title=${this.getAudioState(m.id || "") ===
                              "playing"
                                ? "Pause audio"
                                : "Play message"}
                              aria-label=${this.getAudioState(m.id || "") ===
                              "playing"
                                ? "Pause audio"
                                : "Play message"}
                            >
                              <span class="hollo-icon-wrapper">
                                ${m.loadingVoice
                                  ? html`<span
                                      class="hollo-spinner-small"
                                      aria-hidden="true"
                                    ></span>`
                                  : this.getAudioState(m.id || "") === "playing"
                                  ? pauseIconSvg(22)
                                  : this.getAudioState(m.id || "") === "paused"
                                  ? playIconSvg(22)
                                  : figmaVolumeMaxSvg(22)}
                              </span>
                            </button>`
                          : null}
                        ${this.live
                          ? html`<button
                              class="hollo-message-icon-btn ${this
                                .currentVideoFor === m.id
                                ? "hollo-video-active"
                                : ""}"
                              @click=${() =>
                                this.dispatchEvent(
                                  new CustomEvent("request-video", {
                                    detail: { chatMessageId: m.id },
                                    bubbles: true,
                                    composed: true,
                                  })
                                )}
                              title=${this.currentVideoFor === m.id
                                ? "Stop video"
                                : "Show video for this message"}
                              aria-label=${this.currentVideoFor === m.id
                                ? "Stop video"
                                : "Show video for this message"}
                            >
                              <span class="hollo-icon-wrapper">
                                ${m.loadingVideo
                                  ? html`<span
                                      class="hollo-spinner-small"
                                      aria-hidden="true"
                                    ></span>`
                                  : this.currentVideoFor === m.id
                                  ? stopIconSvg(22)
                                  : videoSvg(18)}
                              </span>
                            </button>`
                          : null}
                      </div>
                      ${m.created_at
                        ? html`<div class="hollo-message-timestamp">
                            ${this.formatTimestamp(m.created_at) || ""}
                          </div>`
                        : null}
                    </div>`
                  : m.is_human
                  ? html`<div
                      class="hollo-message-footer hollo-user-message-footer"
                    >
                      ${m.created_at
                        ? html`<div class="hollo-message-timestamp">
                            ${this.formatTimestamp(m.created_at) || ""}
                          </div>`
                        : null}
                    </div>`
                  : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div
        class="hollo-landing ${this.live ? "hollo-live-landing-height" : ""}"
      >
        <div class="hollo-messages" aria-live="polite">
          ${(() => {
            // Show landing page if no messages exist or all messages are greetings
            const messages = this.messages || [];
            const nonGreetingMessages = this.getNonGreetingMessages();
            // Show landing page when: no messages OR only greeting messages exist, AND not currently typing
            // This ensures that after a new chat and page reload, the landing page is shown
            const hasNoChatMessages = messages.length === 0 || nonGreetingMessages.length === 0;
            const shouldShowLanding = hasNoChatMessages && !this.typing;
            return shouldShowLanding;
          })()
            ? !this.live
              ? html`
                  <div class="hollo-empty-container">
                    <div class="hollo-landing-header">
                      <div class="hollo-sparkle-icon">
                        ${sparklesIcon(24, "#1E1E1E")}
                      </div>
                      <h1 class="hollo-header-title">
                        Ask Our AI Rep
                        <span class="hollo-highlight">anything...</span>
                      </h1>
                    </div>
                    ${this.suggestionsLoading || this.suggestions.length > 0
                      ? html`
                          <div class="hollo-suggestions-section">
                            <div class="hollo-suggestions-grid">
                              ${this.suggestionsLoading
                                ? // Show skeleton loaders while loading
                                  Array.from(
                                    { length: 4 },
                                    (_) => html`
                                      <div
                                        class="hollo-suggestion-skeleton"
                                        role="status"
                                      >
                                        <div
                                          class="hollo-skeleton-line hollo-skeleton-line-1"
                                        ></div>
                                        <div
                                          class="hollo-skeleton-line hollo-skeleton-line-2"
                                        ></div>
                                        <div
                                          class="hollo-skeleton-line hollo-skeleton-line-3"
                                        ></div>
                                      </div>
                                    `
                                  )
                                : // Show actual suggestions when loaded
                                  this.getSuggestionsToDisplay().map(
                                    (suggestion) => html`
                                      <button
                                        class="hollo-suggestion-btn"
                                        @click=${() =>
                                          this.handleSuggestionClick(
                                            suggestion
                                          )}
                                      >
                                        ${suggestion}
                                      </button>
                                    `
                                  )}
                            </div>
                          </div>
                        `
                      : null}
                  </div>
                `
              : html`<div class="hollo-empty-container"></div>`
            : html`
                ${(() => {
                  const rendered = new Set<string>();
                  const parts: unknown[] = [];
                  // Filter out greeting messages when rendering
                  const messagesToRender = (this.messages || []).filter((m) => {
                    const msg = m as UiMessage & { additional_type?: string };
                    return msg.additional_type !== AdditionalTypes.AI_GREETING;
                  });
                  
                  // If no messages to render, show landing page instead
                  if (messagesToRender.length === 0 && !this.typing) {
                    return !this.live
                      ? html`
                          <div class="hollo-empty-container">
                            <div class="hollo-landing-header">
                              <div class="hollo-sparkle-icon">
                                ${sparklesIcon(24, "#1E1E1E")}
                              </div>
                              <h1 class="hollo-header-title">
                                Ask Our AI Rep
                                <span class="hollo-highlight">anything...</span>
                              </h1>
                            </div>
                            ${this.suggestionsLoading || this.suggestions.length > 0
                              ? html`
                                  <div class="hollo-suggestions-section">
                                    <div class="hollo-suggestions-grid">
                                      ${this.suggestionsLoading
                                        ? Array.from(
                                            { length: 4 },
                                            (_) => html`
                                              <div
                                                class="hollo-suggestion-skeleton"
                                                role="status"
                                              >
                                                <div
                                                  class="hollo-skeleton-line hollo-skeleton-line-1"
                                                ></div>
                                                <div
                                                  class="hollo-skeleton-line hollo-skeleton-line-2"
                                                ></div>
                                                <div
                                                  class="hollo-skeleton-line hollo-skeleton-line-3"
                                                ></div>
                                              </div>
                                            `
                                          )
                                        : this.getSuggestionsToDisplay().map(
                                            (suggestion) => html`
                                              <button
                                                class="hollo-suggestion-btn"
                                                @click=${() =>
                                                  this.handleSuggestionClick(
                                                    suggestion
                                                  )}
                                              >
                                                ${suggestion}
                                              </button>
                                            `
                                          )}
                                    </div>
                                  </div>
                                `
                              : null}
                          </div>
                        `
                      : html`<div class="hollo-empty-container"></div>`;
                  }
                  
                  for (const m of messagesToRender) {
                    if (!m || !m.id) continue;
                    if (!m.is_human && m.custom_id && rendered.has(m.id)) {
                      continue;
                    }
                    parts.push(this.renderMessage(m));
                    if (m.is_human && m.custom_id) {
                      for (const r of messagesToRender) {
                        if (
                          r &&
                          !r.is_human &&
                          r.custom_id &&
                          r.custom_id === m.custom_id
                        ) {
                          rendered.add(r.id!);
                          parts.push(this.renderMessage(r));
                        }
                      }
                    }
                  }
                  return parts;
                })()}
                ${this.typing
                  ? html`<div class="hollo-row hollo-left">
                      <div class="hollo-bubble-box">
                        <div class="hollo-bubble hollo-typing">
                          <span class="hollo-dot"></span
                          ><span class="hollo-dot"></span
                          ><span class="hollo-dot"></span>
                        </div>
                      </div>
                    </div>`
                  : null}
              `}
        </div>

        <div
          class="hollo-composer ${this.showRecorder
            ? "hollo-recorder-active"
            : ""}"
        >
          ${this.showRecorder
            ? html`
                <div class="hollo-recorderBarContainer">
                  <div class="hollo-recorderBar">
                    <div
                      aria-hidden="true"
                      class="hollo-recorderBarBorder"
                    ></div>
                    <div class="hollo-recorderBarInner">
                      <div class="hollo-recorderBarContent">
                        <button
                          class="hollo-recorderBinButton"
                          title="Cancel recording"
                          aria-label="Cancel recording"
                          @click=${this.closeRecorder}
                        >
                          <div class="hollo-recorderBinIcon">
                            ${deleteIconSvg()}
                          </div>
                        </button>
                        <div class="hollo-recordingDot hollo-blink">
                          ${recordingDotSvg()}
                        </div>
                        <div class="hollo-timerWaveformContainer">
                          <div class="hollo-timerText">
                            <p>${this.formatSeconds(this.recordingSeconds)}</p>
                          </div>
                          <div
                            class="hollo-waveform ${this.isUserTalking()
                              ? "hollo-talking"
                              : ""}"
                          >
                            ${this.waveformLevels.map(
                              (level) =>
                                html`<span
                                  class="hollo-waveform-bar"
                                  style="height:${Math.max(4, level)}%"
                                ></span>`
                            )}
                          </div>
                        </div>
                        <div class="hollo-recorderSendContainer">
                          <button
                            class="hollo-recorderSendButton"
                            title="Send recording"
                            aria-label="Send recording"
                            @click=${() => this.triggerRecorderSend()}
                          >
                            <div class="hollo-recorderUpIcon">
                              ${sendUpIconSvg()}
                            </div>
                            <span class="hollo-recorderSend-text">Send</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hollo-inline-recorder
                    style="display:none"
                    .open=${this.showRecorder}
                    .bands=${this.waveformBarCount}
                    @send-audio=${(
                      e: CustomEvent<{ base64: string; mimeType: string }>
                    ) => this.onSendAudio(e)}
                    @cancel-recorder=${this.closeRecorder}
                  ></hollo-inline-recorder>
                </div>
              `
            : html`
                <div class="hollo-messageBarContainer">
                  <div class="hollo-messageBar">
                    <div
                      aria-hidden="true"
                      class="hollo-messageBarBorder"
                    ></div>

                    <div class="hollo-messageBarInner">
                      <div class="hollo-messageBarContent">
                        <div class="hollo-messageBarText">
                          <textarea
                            class="hollo-message-input hollo-message-textarea"
                            placeholder="Ask me anything..."
                            .value=${this.message}
                            @input=${(e: Event) => {
                              this.onInput(e);
                              this.autoResizeTextarea(
                                e.target as HTMLTextAreaElement
                              );
                            }}
                            @keydown=${(e: KeyboardEvent) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                this.send();
                              }
                            }}
                            rows="1"
                          ></textarea>
                        </div>
                        <!-- Gradient-minimalist style: mic + phone + send buttons -->
                        ${!this.message.trim()
                          ? html`
                              <button
                                class="hollo-icon-btn hollo-mic"
                                title="Voice input"
                                aria-label="Voice input"
                                @click=${this.openRecorder}
                                ?disabled=${this.showRecorder}
                              >
                                ${micIconSvg(
                                  24,
                                  "var(--hollo-mic-icon-color, #9ca3af)",
                                  1
                                )}
                              </button>
                              <button
                                class="hollo-icon-btn hollo-phone"
                                title="Start voice call"
                                aria-label="Start voice call"
                                @click=${this.startCallFromWave}
                              >
                                ${phoneIconSvg()}
                                <span class="hollo-phone-text">Call</span>
                              </button>
                            `
                          : html`<button
                              class="hollo-icon-btn hollo-send"
                              title="Send"
                              aria-label="Send message"
                              @click=${() => this.send()}
                            >
                              ${sendMinimalSvg(20, "white")}
                            </button>`}
                      </div>
                    </div>
                  </div>
                </div>
              `}
        </div>
      </div>
    `;
  }

  private autoResizeTextarea(textarea: HTMLTextAreaElement) {
    textarea.style.height = "auto";
    const newHeight = Math.min(textarea.scrollHeight, 120); // max-height matches CSS
    textarea.style.height = `${newHeight}px`;
  }

  protected firstUpdated(
    changedProperties: Map<string | number | symbol, unknown>
  ) {
    super.firstUpdated(changedProperties);

    // Initial sync of already present messages
    if (this.messages && this.messages.length > 0) {
      this.messages.forEach((m) => {
        if (m.id) this.streamedMessageIds.add(m.id);
      });
      this.requestUpdate();
    }

    // Initialize textarea height on first render
    const textarea = this.shadowRoot?.querySelector(
      ".hollo-message-textarea"
    ) as HTMLTextAreaElement;
    if (textarea) {
      this.autoResizeTextarea(textarea);
    }
  }

  protected shouldAutoScroll(): boolean {
    // Only auto-scroll on chat page (when there are non-greeting messages)
    // Don't scroll on suggestions page (when only greetings or no messages)
    const nonGreetingMessages = this.getNonGreetingMessages();
    return nonGreetingMessages.length > 0;
  }

  protected updated(changed: Map<string, unknown>) {
    super.updated(changed);
    this.handleUpdated(changed);

    // If messages arrive for the first time after mount (e.g. async load)
    // and there's more than one message, treat it as history load
    if (changed.has("messages")) {
      const oldVal = changed.get("messages") as UiMessage[] | undefined;
      const isInitialSync = !oldVal || oldVal.length === 0;

      if (isInitialSync && this.messages.length > 0) {
        let hasNewTarget = false;
        this.messages.forEach((m) => {
          if (m.id && !this.streamedMessageIds.has(m.id)) {
            this.streamedMessageIds.add(m.id);
            hasNewTarget = true;
          }
        });
        if (hasNewTarget) this.requestUpdate();
      }
    }

    // Auto-resize textarea when message changes
    if (changed.has("message")) {
      const textarea = this.shadowRoot?.querySelector(
        ".hollo-message-textarea"
      ) as HTMLTextAreaElement;
      if (textarea) {
        this.autoResizeTextarea(textarea);
      }
    }

    // Fetch suggestions when we should show them (no messages and not typing)
    if (changed.has("messages") || changed.has("typing")) {
      const nonGreetingMessages = this.getNonGreetingMessages();
      if (nonGreetingMessages.length === 0 && !this.typing && !this.live) {
        this.fetchUrlSuggestions();
      }
    }
  }

  private handleSuggestionClick(text: string) {
    this.message = text;
    // Send the message immediately
    this.send();
  }

  connectedCallback() {
    super.connectedCallback();
    this.handleConnected();
    // Fetch suggestions when component is connected
    this.fetchUrlSuggestions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.handleDisconnected();
  }

  static get styles() {
    return [holloLandingStylesGradientMinimalist];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "hollo-landing-gradient-minimalist": HolloLandingGradientMinimalist;
  }
}
